import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import apiClient from '../services/api';

export interface Objet {
  id: string;
  nom: string;
  categorie: string;
  sousCategorie?: string;
  quantite: number;
  etat: 'bon' | 'à réparer' | 'incomplet' | 'manquant' | 'hs' | 'à vérifier';
  ficheStatut?: 'brouillon' | 'valide';
  besoinAchat: boolean;
  remarques?: string;
  sku?: string;
  zoneId?: string;
  boxId?: string;
  pieceId?: string;
  inventorie?: boolean;
  dateInventaire?: string;
  inventorieParId?: string;
  createdAt: string;
  updatedAt: string;
  zone?: {
    id: string;
    code: string;
    nom: string;
    theme?: string;
  };
  box?: {
    id: string;
    code: string;
    nom: string;
  };
  photos: Array<{
    id: string;
    url: string;
    s3Key?: string;
  }>;
  photoCount?: number;
  user: {
    id: string;
    name?: string;
    email: string;
  };
}

export interface ObjetsFilters {
  search?: string;
  categorie?: string;
  sousCategorie?: string;
  etat?: string;
  couleurPrincipale?: string;
  couleurSecondaire?: string;
  besoinAchat?: boolean;
  sansPhoto?: boolean;
  sansBox?: boolean;
  sansLieu?: boolean;
  zoneId?: string;
  boxId?: string;
  ficheStatut?: 'brouillon' | 'valide';
  page?: number;
  limit?: number;
}

export interface ObjetsResponse {
  objets: Objet[];
  total: number;
  page: number;
  limit: number;
  lastPage: number;
}

export const useObjets = (filters: ObjetsFilters = {}) => {
  return useQuery({
    queryKey: ['objets', filters],
    queryFn: async (): Promise<ObjetsResponse> => {
      const params = new URLSearchParams();
      
      if (filters.search) params.append('search', filters.search);
      if (filters.categorie) params.append('categorie', filters.categorie);
      if (filters.sousCategorie) params.append('sousCategorie', filters.sousCategorie);
      if (filters.etat) params.append('etat', filters.etat);
      if (filters.couleurPrincipale) params.append('couleurPrincipale', filters.couleurPrincipale);
      if (filters.couleurSecondaire) params.append('couleurSecondaire', filters.couleurSecondaire);
      if (filters.besoinAchat !== undefined) params.append('besoinAchat', filters.besoinAchat.toString());
      if (filters.sansPhoto !== undefined) params.append('sansPhoto', filters.sansPhoto.toString());
      if (filters.sansBox !== undefined) params.append('sansBox', filters.sansBox.toString());
      if (filters.sansLieu !== undefined) params.append('sansLieu', filters.sansLieu.toString());
      if (filters.zoneId) params.append('zoneId', filters.zoneId);
      if (filters.boxId) params.append('boxId', filters.boxId);
      if (filters.ficheStatut) params.append('ficheStatut', filters.ficheStatut);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());

      const response = await apiClient.get(`/objets?${params.toString()}`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

export const useObjetPhotoPreview = (
  objetId: string,
  enabled: boolean,
  size: 'thumb' | 'preview' | 'full' = 'thumb',
) => {
  return useQuery({
    queryKey: ['objet-photo-preview', objetId, size],
    queryFn: async (): Promise<string | null> => {
      const response = await apiClient.get(`/objets/${objetId}/photos/preview`, {
        params: { size },
      });
      return response.data?.url ?? null;
    },
    enabled: enabled && !!objetId,
    staleTime: 30 * 60 * 1000,
  });
};

export const useObjet = (id: string) => {
  return useQuery({
    queryKey: ['objet', id],
    queryFn: async (): Promise<Objet> => {
      const response = await apiClient.get(`/objets/${id}`);
      return response.data;
    },
    enabled: !!id,
    refetchOnMount: 'always',
  });
};

export const useCreateObjet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<Objet>) => {
      const response = await apiClient.post('/objets', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['objets'] });
    },
  });
};

export const useUpdateObjet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Objet> }) => {
      if (!id || String(id).trim() === '' || String(id) === 'undefined') {
        throw new Error('Identifiant objet manquant pour la mise à jour');
      }
      const response = await apiClient.patch(`/objets/${id}`, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['objets'] });
      queryClient.invalidateQueries({ queryKey: ['objet', id] });
    },
  });
};

export const useSyncObjetPhotos = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, photos }: { id: string; photos: string[] }) => {
      const response = await apiClient.post(`/objets/${id}/photos`, { photos });
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['objets'] });
      queryClient.invalidateQueries({ queryKey: ['objet', id] });
      queryClient.invalidateQueries({ queryKey: ['objet-photo-preview', id] });
    },
  });
};

export const useDeleteObjet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/objets/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['objets'] });
    },
  });
};

export const useMoveObjet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      id, 
      nouvelleZoneId, 
      nouvelleBoxId 
    }: { 
      id: string; 
      nouvelleZoneId?: string; 
      nouvelleBoxId?: string; 
    }) => {
      const response = await apiClient.post(`/objets/${id}/mouvement`, {
        nouvelleZoneId,
        nouvelleBoxId,
      });
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['objets'] });
      queryClient.invalidateQueries({ queryKey: ['objet', id] });
    },
  });
};

export const useMarquerInventorie = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, userId }: { id: string; userId: string }) => {
      const response = await apiClient.patch(`/objets/${id}/inventorier`, { userId });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['objets'] });
    },
  });
};

export const useMarquerInventorieBulk = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ objetIds, userId }: { objetIds: string[]; userId: string }) => {
      const response = await apiClient.patch(`/objets/bulk/inventorier`, { objetIds, userId });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['objets'] });
    },
  });
};

export const useUpdateBulk = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ objetIds, updates }: { objetIds: string[]; updates: Partial<Objet> }) => {
      const response = await apiClient.put(`/objets/bulk/update`, { objetIds, updates });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['objets'] });
    },
  });
};

export const useDeleteBulk = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (objetIds: string[]) => {
      const response = await apiClient.delete(`/objets/bulk/delete`, { data: { objetIds } });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['objets'] });
    },
  });
};

export const useMoveBulk = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ objetIds, zoneId, boxId }: { objetIds: string[]; zoneId?: string; boxId?: string }) => {
      const response = await apiClient.patch(`/objets/bulk/move`, {
        objetIds,
        nouvelleZoneId: zoneId,
        nouvelleBoxId: boxId,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['objets'] });
      queryClient.invalidateQueries({ queryKey: ['zones'] });
      queryClient.invalidateQueries({ queryKey: ['boxes'] });
    },
  });
};

export interface BulkFromPhotosPayload {
  photos: string[];
  zoneId?: string;
  boxId?: string;
  pieceId?: string;
}

export interface BulkFromPhotosResponse {
  success: boolean;
  count: number;
  objets: Objet[];
}

export const useCreateBulkFromPhotos = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: BulkFromPhotosPayload): Promise<BulkFromPhotosResponse> => {
      const response = await apiClient.post('/objets/bulk/from-photos', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['objets'] });
      queryClient.invalidateQueries({ queryKey: ['zones'] });
      queryClient.invalidateQueries({ queryKey: ['boxes'] });
    },
  });
};

